import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useApi } from "@/lib/api";
import { PageHeader, Panel, PageLoader, StatusBadge, EmptyState, Btn, TextInput } from "@/lib/ui";
import { Inbox, Mail, Phone, Send, User, Clock, Plus, Trash2, MessageSquareText } from "lucide-react";
import { cn } from "@/lib/utils";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const STATUSES = ["NEW", "CONTACTED", "QUALIFIED", "WON", "LOST"];
const TONE = { NEW: "warn", CONTACTED: "gold", QUALIFIED: "muted", WON: "ok", LOST: "err" };
const fmt = (v) => (v ? new Date(v).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

function LeadDetail({ lead, onUpdated }) {
  const api = useApi();
  const qc = useQueryClient();
  const [status, setStatus] = useState(lead.lead_status || "NEW");
  const [assignee, setAssignee] = useState(lead.assignee || "");
  const [notes, setNotes] = useState(lead.notes || "");
  const [subject, setSubject] = useState(`Re: your enquiry with Gopi Gadhvi Group`);
  const [message, setMessage] = useState(`Hi ${lead.name},\n\nThank you for reaching out to the Gopi Gadhvi Group. `);

  const invalidate = () => { qc.invalidateQueries({ queryKey: ["leads"] }); qc.invalidateQueries({ queryKey: ["lead-stats"] }); };
  const { data: snippets = [] } = useQuery({ queryKey: ["reply-snippets"], queryFn: () => api.get("/reply-snippets") });
  const applySnippet = (s) => {
    const fn = (lead.name || "there").split(" ")[0];
    const fill = (t) => (t || "").replaceAll("{{first_name}}", fn).replaceAll("{{name}}", lead.name || "");
    setSubject(fill(s.subject));
    setMessage(fill(s.body));
  };

  const save = useMutation({
    mutationFn: () => api.patch(`/leads/${lead.id}`, { lead_status: status, assignee, notes }),
    onSuccess: (d) => { toast.success("Lead updated"); invalidate(); onUpdated?.(d); },
    onError: (e) => toast.error(e.message),
  });
  const reply = useMutation({
    mutationFn: () => api.post(`/leads/${lead.id}/reply`, { subject, message }),
    onSuccess: (d) => { toast.success(`Email sent to ${lead.email}`); setMessage(`Hi ${lead.name},\n\n`); invalidate(); onUpdated?.(d); setStatus(d.lead_status || status); },
    onError: (e) => toast.error(e.message),
  });

  return (
    <div className="space-y-5" data-testid="lead-detail">
      <div className="space-y-1.5">
        <div className="flex items-center gap-2 text-sm"><User className="h-4 w-4 text-primary" /><span className="font-medium">{lead.name}</span></div>
        <a href={`mailto:${lead.email}`} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"><Mail className="h-4 w-4" />{lead.email}</a>
        {lead.phone && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Phone className="h-4 w-4" />{lead.phone}</div>}
        <div className="flex items-center gap-2 text-xs text-muted-foreground"><Clock className="h-3.5 w-3.5" />{fmt(lead.created_at)}</div>
      </div>

      {lead.message && (
        <div className="border-l-2 border-primary pl-3 text-sm text-foreground/85 bg-surface/50 py-2 rounded-r">{lead.message}</div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[11px] uppercase tracking-[0.1em] text-muted-foreground">Status</label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="lead-status-select" className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} data-testid={`lead-status-${s}`}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <TextInput label="Assignee (email)" value={assignee} placeholder="teammate@gopigadhvi.com" onChange={(e) => setAssignee(e.target.value)} data-testid="lead-assignee" />
      </div>
      <div>
        <label className="text-[11px] uppercase tracking-[0.1em] text-muted-foreground">Internal notes</label>
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="mt-1" data-testid="lead-notes" placeholder="Context for the team…" />
      </div>
      <Btn onClick={() => save.mutate()} disabled={save.isPending} data-testid="lead-save">{save.isPending ? "Saving…" : "Save changes"}</Btn>

      <div className="border-t border-border pt-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-[0.1em] text-muted-foreground">Reply by email</div>
        </div>
        {snippets.length > 0 && (
          <div className="flex flex-wrap gap-1.5" data-testid="lead-snippets">
            {snippets.map((s) => (
              <button key={s.id} onClick={() => applySnippet(s)} data-testid={`lead-snippet-${s.id}`}
                className="text-xs rounded-full px-2.5 py-1 border border-border text-muted-foreground hover:text-primary hover:border-primary/50 transition-colors flex items-center gap-1">
                <MessageSquareText className="h-3 w-3" />{s.title}
              </button>
            ))}
          </div>
        )}
        <TextInput value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="lead-reply-subject" />
        <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={5} data-testid="lead-reply-message" />
        <Btn onClick={() => reply.mutate()} disabled={reply.isPending || !message.trim()} data-testid="lead-reply-send"><Send className="h-4 w-4" /> {reply.isPending ? "Sending…" : "Send reply"}</Btn>
      </div>

      {(lead.replies || []).length > 0 && (
        <div className="border-t border-border pt-4 space-y-2">
          <div className="text-[11px] uppercase tracking-[0.1em] text-muted-foreground">Reply history</div>
          {lead.replies.map((r, i) => (
            <div key={i} className="text-xs bg-surface/50 rounded-md p-2.5 border border-border/50" data-testid={`lead-reply-history-${i}`}>
              <div className="text-foreground/90 font-medium">{r.subject}</div>
              <div className="text-muted-foreground mt-1 whitespace-pre-wrap">{r.message}</div>
              <div className="text-muted-foreground/60 mt-1.5">{r.by} · {fmt(r.at)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SnippetManager() {
  const api = useApi();
  const qc = useQueryClient();
  const { data: snippets = [] } = useQuery({ queryKey: ["reply-snippets"], queryFn: () => api.get("/reply-snippets") });
  const [title, setTitle] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const inval = () => qc.invalidateQueries({ queryKey: ["reply-snippets"] });
  const add = useMutation({
    mutationFn: () => api.post("/reply-snippets", { title, subject, body }),
    onSuccess: () => { toast.success("Snippet saved"); setTitle(""); setSubject(""); setBody(""); inval(); },
    onError: (e) => toast.error(e.message),
  });
  const del = useMutation({
    mutationFn: (id) => api.del(`/reply-snippets/${id}`),
    onSuccess: () => { toast.success("Snippet removed"); inval(); },
    onError: (e) => toast.error(e.message),
  });
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Btn variant="outline" size="sm" data-testid="manage-snippets-btn"><MessageSquareText className="h-4 w-4" /> Snippets</Btn>
      </DialogTrigger>
      <DialogContent className="bg-surface border-border max-w-lg">
        <DialogHeader><DialogTitle>Reply snippets</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground -mt-1">Use <span className="font-mono text-primary">{"{{first_name}}"}</span> to auto-fill the lead's name when applied.</p>
        <div className="space-y-2 max-h-48 overflow-y-auto" data-testid="snippet-list">
          {snippets.length === 0 && <p className="text-xs text-muted-foreground">No snippets yet.</p>}
          {snippets.map((s) => (
            <div key={s.id} className="flex items-start justify-between gap-2 border border-border/50 rounded-md p-2.5" data-testid={`snippet-item-${s.id}`}>
              <div className="min-w-0">
                <div className="text-sm font-medium">{s.title}</div>
                <div className="text-xs text-muted-foreground truncate">{s.subject}</div>
              </div>
              <button onClick={() => del.mutate(s.id)} data-testid={`snippet-delete-${s.id}`} className="text-muted-foreground hover:text-err shrink-0"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
        <div className="border-t border-border pt-3 space-y-2">
          <TextInput placeholder="Snippet title (e.g. Warm intro)" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="snippet-title" />
          <TextInput placeholder="Email subject" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="snippet-subject" />
          <Textarea placeholder="Email body… use {{first_name}}" rows={4} value={body} onChange={(e) => setBody(e.target.value)} data-testid="snippet-body" />
          <Btn onClick={() => add.mutate()} disabled={add.isPending || !title.trim() || !subject.trim() || !body.trim()} data-testid="snippet-save"><Plus className="h-4 w-4" /> Save snippet</Btn>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function LeadInbox() {
  const api = useApi();
  const [filter, setFilter] = useState("ALL");
  const [selected, setSelected] = useState(null);
  const { data: stats } = useQuery({ queryKey: ["lead-stats"], queryFn: () => api.get("/leads/stats") });
  const { data: leads = [], isLoading } = useQuery({ queryKey: ["leads", filter], queryFn: () => api.get(`/leads?status=${filter}`) });

  return (
    <div data-testid="lead-inbox-page">
      <PageHeader kicker="Govern · Concierge" title="Lead Inbox">
        <div className="flex items-center gap-2">
          <SnippetManager />
          <StatusBadge tone="gold" dot>{stats?.total ?? 0} leads</StatusBadge>
        </div>
      </PageHeader>

      <div className="flex flex-wrap gap-2 mb-4" data-testid="lead-filters">
        {["ALL", ...STATUSES].map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`lead-filter-${s}`}
            className={cn("text-xs rounded-full px-3 py-1.5 border transition-colors",
              filter === s ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground hover:text-foreground")}>
            {s}{s !== "ALL" && stats ? ` · ${stats.by_status?.[s] ?? 0}` : ""}
          </button>
        ))}
      </div>

      {isLoading ? <PageLoader /> : leads.length === 0 ? (
        <Panel className="p-10"><EmptyState icon={Inbox} title="No leads here" hint="Leads captured by the public concierge will appear in this inbox." /></Panel>
      ) : (
        <Panel className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="lead-table">
              <thead>
                <tr className="border-b border-border text-left text-[11px] uppercase tracking-[0.1em] text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">Contact</th>
                  <th className="px-4 py-3 font-medium">Message</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Assignee</th>
                  <th className="px-4 py-3 font-medium whitespace-nowrap">Received</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id} onClick={() => setSelected(l)} data-testid={`lead-row-${l.id}`}
                    className="border-b border-border/50 hover:bg-accent/40 transition-colors cursor-pointer">
                    <td className="px-4 py-3 font-medium whitespace-nowrap">{l.name}</td>
                    <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{l.email}{l.phone ? <div className="text-xs">{l.phone}</div> : null}</td>
                    <td className="px-4 py-3 text-muted-foreground max-w-xs truncate">{l.message || "—"}</td>
                    <td className="px-4 py-3"><StatusBadge tone={TONE[l.lead_status] || "muted"}>{l.lead_status || "NEW"}</StatusBadge></td>
                    <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{l.assignee || <span className="text-muted-foreground/50">Unassigned</span>}</td>
                    <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{fmt(l.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto bg-surface border-border">
          <SheetHeader><SheetTitle className="text-left">Lead</SheetTitle></SheetHeader>
          {selected && <div className="mt-4"><LeadDetail lead={selected} onUpdated={setSelected} /></div>}
        </SheetContent>
      </Sheet>
    </div>
  );
}
